import {
  clearStoredTokens,
  loadStoredTokens,
  saveStoredTokens,
} from "./tokens";
import type { AppAuthSession } from "./types";

import { apiOrigin as sharedApiOrigin } from "@/utils/apiBase";

export function authApiUrl(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  const origin = sharedApiOrigin();
  return origin ? `${origin}/api/auth${p}` : `/api/auth${p}`;
}

function decodeJwtExp(accessToken: string): number {
  try {
    const part = accessToken.split(".")[1];
    if (!part) return Math.floor(Date.now() / 1000) + 900;
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.exp === "number" ? json.exp : Math.floor(Date.now() / 1000) + 900;
  } catch {
    return Math.floor(Date.now() / 1000) + 900;
  }
}

export async function authFetchJson<T>(
  path: string,
  init: RequestInit & { accessToken?: string | null } = {}
): Promise<T> {
  const { accessToken, ...rest } = init;
  const headers: Record<string, string> = {
    ...(rest.headers as Record<string, string>),
  };
  if (!headers["Content-Type"] && rest.body) {
    headers["Content-Type"] = "application/json";
  }
  const token = accessToken ?? loadStoredTokens().access;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  let res: Response;
  try {
    res = await fetch(authApiUrl(path), { ...rest, headers });
  } catch {
    throw new Error(
      "Could not reach the auth API. Run `npm run dev` (API :8021, Vite :8080) and open http://127.0.0.1:8080. Tip: leave `VITE_API_ORIGIN` unset in dev so `/api` uses the Vite proxy; if it is set, the API must allow your browser origin (localhost vs 127.0.0.1 differ)."
    );
  }
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || res.statusText);
  }
  return data as T;
}

export async function refreshTokens(): Promise<{
  access_token: string;
  refresh_token: string;
} | null> {
  const { refresh } = loadStoredTokens();
  if (!refresh) return null;
  try {
    const data = await authFetchJson<{
      access_token: string;
      refresh_token: string;
    }>("/refresh", {
      method: "POST",
      body: JSON.stringify({ refresh_token: refresh }),
      accessToken: null,
    });
    saveStoredTokens(data.access_token, data.refresh_token);
    return data;
  } catch {
    clearStoredTokens();
    return null;
  }
}

export function sessionFromAuthResponse(data: {
  access_token: string;
  refresh_token: string;
  user: { id: string; email: string };
}): AppAuthSession {
  saveStoredTokens(data.access_token, data.refresh_token);
  return {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    expires_at: decodeJwtExp(data.access_token),
    user: {
      id: data.user.id,
      email: data.user.email,
    },
  };
}

export async function logoutRemote(refreshToken: string | null) {
  if (!refreshToken) return;
  try {
    await authFetchJson("/logout", {
      method: "POST",
      body: JSON.stringify({ refresh_token: refreshToken }),
      accessToken: null,
    });
  } catch {
    /* ignore */
  }
}
